import { useQuery } from '@ltvco/refresh-lib/vendors';

import {
  getCreativeWorksSearchItem,
  searchCreativeWorksCatalog,
} from 'utils/creativeWorksSearch/creativeWorksSearchData';

const CREATIVE_WORKS_SEARCH_QUERY_KEY = 'catalog-search-v2' as const;
const CREATIVE_WORKS_SEARCH_ITEM_QUERY_KEY = 'catalog-search-item' as const;

const CREATIVE_WORKS_SEARCH_MAX_RETRIES = 2;

const DEFAULT_CREATIVE_WORKS_SEARCH_QUERY_OPTIONS = {
  staleTime: 2 * 60 * 1000,
  cacheTime: 10 * 60 * 1000,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
} as const;

export interface UseCreativeWorksSearchQueryArgs {
  query: string;
  originalFormat?: string;
  /** catalog `sp` page number (1-based). */
  page?: number;
  enabled?: boolean;
  onError?: (error: unknown) => void;
}

export const useCreativeWorksSearchQuery = ({
  query,
  originalFormat = '',
  page = 1,
  enabled = true,
  onError,
}: UseCreativeWorksSearchQueryArgs) => {
  const trimmedQuery = query.trim();
  const trimmedFormat = originalFormat.trim();
  const catalogPage = Math.max(1, page);

  return useQuery({
    queryKey: [
      CREATIVE_WORKS_SEARCH_QUERY_KEY,
      trimmedQuery,
      trimmedFormat,
      catalogPage,
    ],
    queryFn: () =>
      searchCreativeWorksCatalog({
        q: trimmedQuery,
        page: catalogPage,
        ...(trimmedFormat ? { original_format: trimmedFormat } : {}),
      }),
    enabled: Boolean(enabled && trimmedQuery.length > 0),
    ...DEFAULT_CREATIVE_WORKS_SEARCH_QUERY_OPTIONS,
    retry: (failureCount: number) =>
      failureCount < CREATIVE_WORKS_SEARCH_MAX_RETRIES,
    onError,
  });
};

export interface UseCreativeWorksSearchItemQueryArgs {
  itemId: string;
  enabled?: boolean;
}

export const useCreativeWorksSearchItemQuery = ({
  itemId,
  enabled = true,
}: UseCreativeWorksSearchItemQueryArgs) => {
  const trimmedItemId = itemId.trim();

  return useQuery({
    queryKey: [CREATIVE_WORKS_SEARCH_ITEM_QUERY_KEY, trimmedItemId],
    queryFn: () => getCreativeWorksSearchItem(trimmedItemId),
    enabled: Boolean(enabled && trimmedItemId),
    ...DEFAULT_CREATIVE_WORKS_SEARCH_QUERY_OPTIONS,
    retry: (failureCount: number) =>
      failureCount < CREATIVE_WORKS_SEARCH_MAX_RETRIES,
  });
};
